import React from 'react';
import { Loader2 } from 'lucide-react';
import './LoadingSpinner.scss';

/**
 * LoadingSpinner - Composant de loading moderne avec lucide-react et daisyUI
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} variant - 'primary' | 'secondary' | 'accent' | 'neutral' | 'info' | 'success' | 'warning' | 'error'
 * @param {boolean} fullscreen - Affichage en plein écran avec overlay
 * @param {string} text - Texte optionnel à afficher
 * @param {boolean} inline - Affichage en ligne (sans overlay)
 */
const LoadingSpinner = ({ 
    size = 'md', 
    variant = 'primary', 
    fullscreen = false, 
    text = '',
    inline = false,
    className = ''
}) => {
    // Tailles DaisyUI compatibles
    const sizeClasses = {
        xs: 'w-4 h-4',
        sm: 'w-5 h-5', 
        md: 'w-6 h-6',
        lg: 'w-8 h-8',
        xl: 'w-12 h-12'
    };

    // Couleurs DaisyUI
    const variantClasses = {
        primary: 'text-primary',
        secondary: 'text-secondary', 
        accent: 'text-accent',
        neutral: 'text-neutral',
        info: 'text-info',
        success: 'text-success',
        warning: 'text-warning',
        error: 'text-error'
    };

    // Tailles de texte DaisyUI
    const textSizeClasses = {
        xs: 'text-xs',
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg', 
        xl: 'text-xl'
    };

    const spinner = (
        <div className={`loading-spinner flex items-center ${className}`}>
            <Loader2 
                className={`animate-spin ${sizeClasses[size]} ${variantClasses[variant]}`}
            />
            {text && (
                <span className={`ml-3 ${textSizeClasses[size]} font-medium text-base-content`}>
                    {text}
                </span>
            )}
        </div>
    );

    // Affichage inline (dans un bouton par exemple)
    if (inline) {
        return <div className="flex items-center justify-center">{spinner}</div>;
    }

    // Affichage fullscreen avec overlay DaisyUI
    if (fullscreen) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                <div className="card bg-base-100 shadow-2xl p-8 mx-4 max-w-sm w-full">
                    <div className="card-body flex flex-col items-center space-y-4 p-0">
                        {spinner}
                    </div>
                </div>
            </div>
        );
    }

    // Affichage normal (dans un container)
    return (
        <div className="flex items-center justify-center p-4">
            {spinner}
        </div>
    );
};

export default LoadingSpinner;
